import React from 'react';
import { pointOnCircle, type CircleLayout } from '@/app/components/divergenceGeometry';
import { arcLength, arcPath, clamp01, dialAngle, ticksAlong } from './geometry';

/**
 * A reading plotted on an arc — the hero ring's idea, stripped to static SVG.
 *
 * The hero's ring is a canvas carrying thousands of animated dots. None of that
 * travels: what makes it recognisable is the arc, the plotted point and the
 * tick marks, and those are a handful of path commands. This renders on the
 * server and ships no JavaScript.
 *
 * `value` is the only thing that must be real. An arc that plots nothing is
 * ornament, and ornament is how this language gets spent.
 */

export interface ArcProps {
  /** The reading, 0 to 1. */
  value: number;
  /** Diameter of the square the arc is drawn into. */
  size?: number;
  /** How much of the circle the track covers, in turns. 0.75 is a dial. */
  sweep?: number;
  /** Where the track begins, in turns clockwise from twelve o'clock. */
  start?: number;
  /** Number of tick marks along the track. 0 for none. */
  ticks?: number;
  onDark?: boolean;
  animate?: boolean;
  /** Rendered in the middle of the dial — the number the arc is showing. */
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** What the arc means, for anyone who cannot see it. */
  label?: string;
}

export default function Arc({
  value,
  size = 200,
  sweep = 0.75,
  start = 0.125,
  ticks = 0,
  onDark,
  animate = false,
  children,
  className,
  style,
  label,
}: ArcProps) {
  const reading = clamp01(value);
  const layout: CircleLayout = { cx: size / 2, cy: size / 2, radius: size / 2 - 14 };

  const track = arcPath(layout, start, start + sweep);
  const filled = arcPath(layout, start, start + sweep * reading);
  const filledLength = arcLength(layout, sweep * reading);
  const head = pointOnCircle(layout, dialAngle(start + sweep * reading));
  const channel = onDark ? 'var(--m-on-ink-rgb)' : 'var(--m-ink-rgb)';
  const ink = onDark ? 'var(--m-on-ink)' : 'var(--m-ink)';

  return (
    <div
      className={className}
      style={{ position: 'relative', width: size, height: size, ...style }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        focusable="false"
      >
        <path d={track} stroke={`rgba(${channel},0.14)`} strokeWidth={1} />

        {ticksAlong(layout, start, start + sweep, ticks, 8).map((outer, i) => {
          const inner = ticksAlong(layout, start, start + sweep, ticks, 2)[i];
          return (
            <line
              key={`${outer.x.toFixed(1)}-${outer.y.toFixed(1)}`}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke={`rgba(${channel},0.22)`}
              strokeWidth={1}
            />
          );
        })}

        {reading > 0 && (
          <>
            <path
              d={filled}
              stroke={ink}
              strokeWidth={2}
              strokeLinecap="round"
              className={animate ? 'instrument-draw' : undefined}
              style={
                animate
                  ? { strokeDasharray: filledLength, strokeDashoffset: filledLength }
                  : undefined
              }
            />
            {/* The plotted point: where the reading actually lands. */}
            <circle cx={head.x} cy={head.y} r={3.5} fill={ink} />
            <circle cx={head.x} cy={head.y} r={7} stroke={`rgba(${channel},0.3)`} strokeWidth={1} />
          </>
        )}
      </svg>

      {children && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
